import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { HiOutlineDocument } from 'react-icons/hi';

import dayjs from 'dayjs';
import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';

import {
  useGetAgreementsByIdQuery,
  useGetInvoicesByIdDocumentQuery,
  useGetAgreementsByAgreementIdInvoiceHistoryQuery,
  useLazyGetInvoicesByIdDocumentQuery,
} from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageDetailContent,
  EmptyPlaceholder,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Spinner,
} from '@client/toolkit';

import { AgreementNameField } from '../components/agreement-name/agreement-name-field.component';
import { PageContentAnimation } from '../../../../components';
import { AgreementWizardActions } from '../components/agreement-wizard-actions.component';
import { useAppSelector } from '../../../../hooks';
import { selectSelectedTenant } from '../../../../redux/session/session.slice';

const formatCurrency = (amount: number, currency = 'NOK'): string => {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD.MM.YYYY');
};

export function AgreementInvoicingRoute() {
  const { t } = useTranslation('agreements');
  const { id } = useParams<{ id: string }>();

  // Check if ERP is configured for the selected tenant from session
  const selectedTenant = useAppSelector(selectSelectedTenant);
  const isErpConfigured = selectedTenant && selectedTenant !== 'all'
    ? !!selectedTenant.erpIntegrations?.some((erp) => erp.configured)
    : false;

  const { data: agreement, isFetching: isAgreementFetching } = useGetAgreementsByIdQuery(
    { id: id ?? '' },
    { skip: id == null }
  );

  const invoiceId = agreement?.invoiceId ?? null;

  // Only fetch invoice if ERP is configured
  const { data: invoicePdfUrl, isLoading: isPdfLoading } = useGetInvoicesByIdDocumentQuery(
    { id: invoiceId! },
    { skip: invoiceId == null || isAgreementFetching || !isErpConfigured }
  );

  // Only show preview if status is Ready AND ERP is configured
  const showPreview = (agreement?.status === 'Ready' || agreement?.status === 'Effective') && isErpConfigured;

  // Invoice history
  const { data: invoiceHistory, isFetching: isHistoryFetching } = useGetAgreementsByAgreementIdInvoiceHistoryQuery(
    { agreementId: id ?? '' },
    { skip: id == null || !isErpConfigured }
  );

  const [getInvoiceDocument] = useLazyGetInvoicesByIdDocumentQuery();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleOpenInvoice = useCallback((invoiceId: string) => {
    setDownloadingId(invoiceId);
    getInvoiceDocument({ id: invoiceId })
      .unwrap()
      .then((blobUrl) => {
        const link = document.createElement('a');
        link.href = blobUrl as string;
        link.download = invoiceId;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        toast.error(t('agreement.invoicing.history.downloadError'), { id: 'invoice-download-error' });
      })
      .finally(() => {
        setDownloadingId(null);
      });
  }, [getInvoiceDocument, t]);

  return (
    <PageDetailContent>
      <PageContentAnimation className="space-y-4 px-4 pt-2 pb-8 rounded-lg shadow-lg border border-border">
        <div className="flex items-center">
          <AgreementNameField className="flex-1" />
          <AgreementWizardActions />
        </div>

        {/* Invoice Preview — shown when status is Ready or beyond */}
        {showPreview && (
          <>
            {/* Green banner */}
            <div className="flex items-start gap-3 rounded-lg border border-green-600 bg-green-950/40 px-4 py-3">
              <HiOutlineDocument className="text-green-500 text-xl mt-0.5 shrink-0" />
              <div>
                <div className="text-green-400 font-semibold text-sm">{t('wizard.invoicePreview.title')}</div>
                <div className="text-green-300 text-xs mt-0.5">{t('wizard.invoicePreview.subtitle')}</div>
              </div>
            </div>

            {/* PDF viewer or placeholder */}
            <div className="bg-white rounded-lg shadow-lg mx-auto w-full max-w-5xl min-h-[1200px] flex flex-col items-center">
              {invoicePdfUrl ? (
                <iframe
                  src={invoicePdfUrl as string}
                  className="w-full h-full min-h-[1200px] rounded-lg"
                  title={t('wizard.invoicePreview.title')}
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-12 mt-[100px]">
                  {isPdfLoading || isAgreementFetching ? (
                    <div className="w-10 h-10 border-2 border-gray-400 rounded-full animate-spin border-r-transparent mb-4" />
                  ) : (
                    <HiOutlineDocument className="text-6xl mb-4 text-gray-300" />
                  )}
                  <p className="text-gray-400 text-lg font-medium">{t('wizard.invoicePreview.placeholder')}</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Invoice History */}
        <Card>
          <CardHeader>
            <CardTitle variant="dense">{t('agreement.invoicing.history.title')}</CardTitle>
          </CardHeader>
          <CardContent variant="fill">
            {isHistoryFetching ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" />
              </div>
            ) : !invoiceHistory?.length ? (
              <EmptyPlaceholder>
                {t('agreement.invoicing.history.placeholder')}
              </EmptyPlaceholder>
            ) : (
              <div className="px-4 pb-4">
                <Table className="w-full table-fixed" size="small">
                  <TableHead>
                    <TableHeadRow>
                      <TableHeadCell className="w-[18%]">{t('agreement.invoicing.history.columns.tenant')}</TableHeadCell>
                      <TableHeadCell className="w-[12%]">{t('agreement.invoicing.history.columns.invoiceNumber')}</TableHeadCell>
                      <TableHeadCell className="w-[14%]">{t('agreement.invoicing.history.columns.invoiceDate')}</TableHeadCell>
                      <TableHeadCell className="w-[14%]">{t('agreement.invoicing.history.columns.dueDate')}</TableHeadCell>
                      <TableHeadCell className="w-[14%]">{t('agreement.invoicing.history.columns.amount')}</TableHeadCell>
                      <TableHeadCell className="w-[14%]">{t('agreement.invoicing.history.columns.outstanding')}</TableHeadCell>
                      <TableHeadCell className="w-[14%]">{t('agreement.invoicing.history.columns.status')}</TableHeadCell>
                    </TableHeadRow>
                  </TableHead>
                  <TableBody>
                    {invoiceHistory.map((invoice) => {
                      const isPaid = invoice.amountOutstanding === 0;
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell className="w-[18%]">
                            <span className="font-medium text-white">{invoice.customerName ?? '-'}</span>
                          </TableCell>
                          <TableCell className="w-[12%]">
                            {downloadingId === invoice.id ? (
                              <span className="inline-flex items-center gap-1 text-brand-primary">
                                <Spinner size="xs" />
                              </span>
                            ) : (
                              <a
                                onClick={() => handleOpenInvoice(invoice.id)}
                                className="text-brand-primary underline cursor-pointer"
                              >
                                #{invoice.invoiceNumber}
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="w-[14%]">
                            <span className="text-white">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '-'}</span>
                          </TableCell>
                          <TableCell className="w-[14%]">
                            <span className="text-white">{invoice.invoiceDueDate ? formatDate(invoice.invoiceDueDate) : '-'}</span>
                          </TableCell>
                          <TableCell className="w-[14%]">
                            <span className="text-white">{formatCurrency(invoice.amount)}</span>
                          </TableCell>
                          <TableCell className="w-[14%]">
                            <span className={invoice.amountOutstanding > 0 ? 'text-yellow-400' : 'text-white'}>
                              {formatCurrency(invoice.amountOutstanding)}
                            </span>
                          </TableCell>
                          <TableCell className="w-[14%]">
                            <span className={isPaid ? 'text-green-400' : 'text-yellow-400'}>
                              {isPaid
                                ? t('agreement.invoicing.history.status.paid')
                                : t('agreement.invoicing.history.status.unpaid')}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Orders */}
        {/* <Card>
          <CardHeader>
            <CardTitle variant="dense">{t('agreement.invoicing.pending.title')}</CardTitle>
          </CardHeader>
          <CardContent variant="fill">
            <EmptyPlaceholder>
              {t('agreement.invoicing.pending.placeholder')}
            </EmptyPlaceholder>
          </CardContent>
        </Card> */}

        {/* Invoice Grouping */}
        <Card>
          <CardHeader>
            <CardTitle variant="dense">{t('agreement.invoicing.grouping.title')}</CardTitle>
          </CardHeader>
          <CardContent variant="fill">
            <EmptyPlaceholder>
              {t('agreement.invoicing.grouping.placeholder')}
            </EmptyPlaceholder>
          </CardContent>
        </Card>
      </PageContentAnimation>
    </PageDetailContent>
  );
}
